import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils"; // Ensure this utility fetches the correct token
import { useNavigate } from "react-router-dom";

const FormsContext = createContext();

export const FormsProvider = ({ children, mode = "all" }) => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [formsPagination, setFormsPagination] = useState({
    meta: {},
    links: {},
  });
  const [internalFormsPagination, setInternalFormsPagination] = useState({
    meta: {},
    links: {},
  });
  const [externalFormsPagination, setExternalFormsPagination] = useState({
    meta: {},
    links: {},
  });

  const [internalForms, setInternalForms] = useState([]);
  const [externalForms, setExternalForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = getToken();

  const API_URL = "https://hrmsnew.prepstripe.com/api/"; // Replace with your actual API URL
  // const token = getToken(); // Retrieve the token

  // Axios instance with default headers
  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }),
    [API_URL], // Recreate the axios instance only when API_URL or token changes
  );

  const fetchForms = async (page = 1) => {
    try {
      setLoading(true);

      const internalRequest = axiosInstance.get(
        `custom-forms-internal?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const externalRequest = axiosInstance.get("custom-forms-external");

      const [internalRes, externalRes] = await Promise.all([
        internalRequest,
        externalRequest,
      ]);

      setInternalForms(internalRes.data.data);
      setExternalForms(externalRes.data.data);
      setForms([...internalRes.data.data, ...externalRes.data.data]);
      setFormsPagination({
        meta: internalRes.data.meta,
        links: internalRes.data.links,
      });
      setInternalFormsPagination({
        meta: internalRes.data.meta,
        links: internalRes.data.links,
      });
      // If your external API supports pagination, update this accordingly
      setExternalFormsPagination({
        meta: {
          current_page: 1,
          last_page: 1,
          total: externalRes.data.data.length,
        },
        links: {},
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch forms");
      console.error("Fetch Forms Error:", err.response || err);
    } finally {
      setLoading(false);
    }
  };

  // fetch internal form - auth required, with pagination
  const fetchInternalForms = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `custom-forms-internal?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setInternalForms(response.data.data);
      setInternalFormsPagination({
        meta: response.data.meta,
        links: response.data.links,
      });
      return response.data.data;
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to fetch internal forms",
      );
      return error.response?.data?.message || "Failed to fetch internal forms";
    } finally {
      setLoading(false);
    }
  };

  // fetch external form - no auth required, with pagination if supported
  const fetchExternalForms = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `custom-forms-external?page=${page}`,
      );
      setExternalForms(response.data.data);
      setExternalFormsPagination({
        meta: response.data.meta || {
          current_page: 1,
          last_page: 1,
          total: response.data.data.length,
        },
        links: response.data.links || {},
      });
      return response.data.data;
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to fetch external forms",
      );
      throw new Error(
        error.response?.data?.message || "Failed to fetch external forms",
      );
    } finally {
      setLoading(false);
    }
  };

  // single form
  const fetchFormById = async (id) => {
    try {
      const response = await axiosInstance.get(`custom-forms/${id}`);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch form");
      console.error("Fetch Form By ID Error:", err.response || err);
      throw err;
    }
  };

  // Create a new form
  const createForm = async (formData) => {
    try {
      const response = await axiosInstance.post("custom-forms", formData);
      // setForms((prev) => [...prev, response.data]);
      fetchForms();
      return response.data;
    } catch (err) {
      return err.response?.data;
    }
  };

  // update a form
  const updateForm = async (id, formData) => {
    try {
      const response = await axiosInstance.put(`custom-forms/${id}`, formData);
      setForms((prev) =>
        prev.map((form) => (form.id === id ? response.data : form)),
      );
      return response.data;
    } catch (err) {
      return err.response?.data;
    }
  };

  // Delete a form
  const deleteForm = async (id) => {
    try {
      await axiosInstance.delete(`custom-forms/${id}`);
      setForms((prev) => prev.filter((form) => form.id !== id));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete form");
      console.error("Delete Form Error:", err.response || err);
      throw err; // Rethrow the error to handle it in the calling component
    }
  };

  const value = useMemo(
    () => ({
      forms,
      internalForms,
      externalForms,
      loading,
      error,
      fetchForms,
      fetchExternalForms,
      fetchInternalForms,
      fetchFormById,
      createForm,
      updateForm,
      deleteForm,
      formsPagination,
      internalFormsPagination,
      externalFormsPagination,
    }),
    [
      forms,
      fetchExternalForms,
      fetchForms,
      fetchInternalForms,
      internalForms,
      externalForms,
      loading,
      error,
      formsPagination,
      internalFormsPagination,
      externalFormsPagination,
    ],
  );

  // Fetch forms on initial load
  useEffect(() => {
    if (mode === "internal") {
      fetchInternalForms();
    } else if (mode === "external") {
      fetchExternalForms();
    } else {
      fetchForms(); // all
    }
  }, [mode]);

  return (
    <FormsContext.Provider value={value}>{children}</FormsContext.Provider>
  );
};

// Custom hook to use FormsContext
export const useForms = () => {
  const context = useContext(FormsContext);
  if (!context) {
    throw new Error("useForms must be used within a FormsProvider");
  }
  return context;
};
